import { SectionLabel } from "./WhatMakesSpecial";
import { promotionGroups } from "@/mocks/promotions";
import { useState } from "react";
import { RegisterLink } from "@/components/RegisterLink";

export default function BonusesSection() {
  const categories = promotionGroups.map((g) => g.title);
  const [activeCategory, setActiveCategory] = useState(() => categories[0] ?? "");

  const activeGroup = promotionGroups.find((g) => g.title === activeCategory) ?? promotionGroups[0];

  return (
    <section id="bonuses" className="max-w-5xl mx-auto px-6 py-12">
      <SectionLabel>Bonuses and Promotions</SectionLabel>
      <p className="text-white/40 text-sm leading-relaxed mb-10 max-w-2xl">
        Rolex9 offers one of the most rewarding bonus ecosystems in Australian online gaming — from VIP programs and progressive deposit bonuses to daily free chips, social share rewards, lucky wheels and weekly cash back. Every player, every day, every deposit has a bonus waiting.
      </p>

      {/* VIP Program highlight banner */}
      <div
        className="relative rounded-2xl p-6 mb-8 overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(201,168,76,0.08) 0%, rgba(107,33,168,0.15) 50%, rgba(26,58,143,0.1) 100%)",
          border: "1px solid rgba(201,168,76,0.25)",
        }}
      >
        <div className="absolute top-0 right-0 w-48 h-48 opacity-10 pointer-events-none"
          style={{ background: "radial-gradient(circle at top right, rgba(201,168,76,0.8), transparent 70%)" }}
        ></div>
        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div
            className="px-4 py-2 rounded-xl flex-shrink-0"
            style={{ background: "linear-gradient(135deg, rgba(201,168,76,0.2), rgba(107,33,168,0.2))", border: "1px solid rgba(201,168,76,0.35)" }}
          >
            <span className="text-[#c9a84c] text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>VIP Program</span>
            <div
              className="text-2xl font-bold"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                background: "linear-gradient(135deg, #c9a84c, #e8c97a)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >AUD 5,999</div>
          </div>
          <div className="flex-1">
            <p className="text-white/80 font-semibold text-base mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Monthly Deposit Ranking — AUD 5,999 Prize Pool</p>
            <p className="text-white/40 text-xs leading-relaxed">Compete in the exclusive monthly deposit ranking. The more you deposit throughout the month, the higher your ranking and the bigger your reward from the AUD 5,999 prize pool.</p>
          </div>
          <RegisterLink section="bonuses" label="Join Ranking" className="btn-glow px-6 py-3 rounded-xl text-xs flex-shrink-0 whitespace-nowrap text-center">Join Ranking</RegisterLink>
        </div>
      </div>

      {/* Left menu + right cards */}
      <div className="grid grid-cols-1 md:grid-cols-[260px_minmax(0,1fr)] gap-6">
        {/* Category Menu (left) */}
        <aside className="md:sticky md:top-24 self-start">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-5 rounded-full" style={{ background: "linear-gradient(180deg, #c9a84c, #6b21a8)" }}></div>
            <h3
              className="text-white/70 font-semibold text-sm tracking-widest uppercase"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Categories
            </h3>
          </div>

          <div
            className="rounded-2xl p-3 overflow-auto"
            style={{
              background: "linear-gradient(135deg, rgba(3,7,33,0.55), rgba(10,5,40,0.55))",
              border: "1px solid rgba(201,168,76,0.16)",
              maxHeight: "min(560px, calc(100vh - 220px))",
            }}
          >
            <div className="relative">
              {/* Mobile edge fades to hint horizontal scroll */}
              <div
                className="md:hidden pointer-events-none absolute inset-y-0 left-0 w-8 z-10"
                style={{ background: "linear-gradient(90deg, rgba(3,7,33,0.85), rgba(3,7,33,0))" }}
              ></div>
              <div
                className="md:hidden pointer-events-none absolute inset-y-0 right-0 w-10 z-10"
                style={{ background: "linear-gradient(270deg, rgba(3,7,33,0.85), rgba(3,7,33,0))" }}
              ></div>

              <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible scrollbar-none md:scrollbar-auto snap-x snap-mandatory md:snap-none scroll-px-3 pr-2">
                {categories.map((title) => {
                  const active = title === activeCategory;
                  return (
                    <button
                      key={title}
                      type="button"
                      onClick={() => setActiveCategory(title)}
                      className="px-4 py-2 rounded-xl text-xs whitespace-nowrap md:whitespace-normal md:text-left transition-colors snap-start"
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        border: active ? "1px solid rgba(201,168,76,0.6)" : "1px solid rgba(201,168,76,0.18)",
                        background: active
                          ? "linear-gradient(135deg, rgba(201,168,76,0.18), rgba(107,33,168,0.16))"
                          : "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
                        color: active ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.55)",
                      }}
                    >
                      {title}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </aside>

        {/* Active Promotion Group (right) */}
        {activeGroup && (
          <div className="mb-10">
          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <div className="w-1 h-5 rounded-full" style={{ background: "linear-gradient(180deg, #c9a84c, #6b21a8)" }}></div>
              <h3
                className="text-white/70 font-semibold text-sm tracking-widest uppercase"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {activeGroup.title}
              </h3>
            </div>
            <span className="text-white/30 text-xs" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {activeGroup.items.length} offer{activeGroup.items.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeGroup.items.map((item) => (
              <div
                key={item.name}
                className="relative rounded-2xl p-5 flex flex-col overflow-hidden animate-border-glow"
                style={{
                  background: "linear-gradient(135deg, rgba(3,7,33,0.8), rgba(10,5,40,0.8))",
                  border: "1px solid rgba(201,168,76,0.1)",
                }}
              >
                <div
                  className="absolute inset-0 opacity-40 pointer-events-none"
                  style={{ background: "linear-gradient(135deg, rgba(201,168,76,0.06), rgba(107,33,168,0.08))" }}
                ></div>

                <div className="relative z-10 flex items-start justify-between mb-4">
                  <h4
                    className="text-white/75 font-semibold text-sm leading-snug flex-1 pr-3"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {item.name}
                  </h4>
                  <span
                    className="text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0 whitespace-nowrap"
                    style={{
                      background: "linear-gradient(135deg, rgba(201,168,76,0.15), rgba(107,33,168,0.15))",
                      border: "1px solid rgba(201,168,76,0.25)",
                      color: "#e8c97a",
                      fontFamily: "'Space Grotesk', sans-serif",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {item.amount}
                  </span>
                </div>

                <p className="relative z-10 text-white/30 text-xs leading-relaxed flex-1 mb-5">{item.desc}</p>

                <RegisterLink
                  section="bonuses"
                  label={`Claim Now - ${item.name}`}
                  className="relative z-10 btn-glow w-full py-2.5 rounded-xl text-xs text-center"
                >
                  Claim Now
                </RegisterLink>
              </div>
            ))}
          </div>
          </div>
        )}
      </div>


    </section>
  );
}
